import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts'
import type { ReportFilters } from '@/lib/reports-engine'
import { useTimeReport } from '@/hooks/useReportsEngine'
import { useWeeklyHeatmap } from '@/hooks/useReportsData'
import { Clock, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

interface TimeReportTabProps {
  filters: ReportFilters
}

const formatHours = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const categoryLabels: Record<string, string> = {
  dev: 'Desenvolvimento',
  meeting: 'Reunião',
  research: 'Pesquisa',
  admin: 'Administrativo',
  other: 'Outro'
}

const categoryColors: Record<string, string> = {
  dev: 'hsl(224, 71%, 40%)',
  meeting: 'hsl(38, 92%, 50%)',
  research: 'hsl(280, 65%, 55%)',
  admin: 'hsl(142, 71%, 45%)',
  other: 'hsl(210, 50%, 60%)'
}

export function TimeReportTab({ filters }: TimeReportTabProps) {
  const { data: timeReport, isLoading: loadingTime } = useTimeReport(filters)
  const { data: heatmapData, isLoading: loadingHeatmap } = useWeeklyHeatmap(filters)

  const isLoading = loadingTime || loadingHeatmap

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-[300px] rounded-xl" />
          <Skeleton className="h-[300px] rounded-xl" />
        </div>
        <Skeleton className="h-[200px] rounded-xl" />
      </div>
    )
  }

  // Prepare billable vs non-billable data
  const hoursData = [
    { 
      name: 'Faturável', 
      hours: timeReport?.billableHours || 0, 
      fill: 'hsl(var(--success))'
    },
    { 
      name: 'Não Faturável', 
      hours: timeReport?.nonBillableHours || 0, 
      fill: 'hsl(var(--muted-foreground))'
    }
  ]

  // Prepare category data
  const categoryData = Object.entries(timeReport?.hoursByCategory || {})
    .map(([key, value]) => ({
      name: categoryLabels[key] || key,
      hours: value,
      fill: categoryColors[key] || 'hsl(var(--muted-foreground))'
    }))
    .sort((a, b) => b.hours - a.hours)

  // Prepare weekday data
  const weekdayData = Object.entries(timeReport?.hoursByWeekday || {})
    .map(([day, hours]) => ({
      day,
      hours,
      fill: day === timeReport?.peakDay ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))'
    }))

  // Prepare heatmap matrix
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
  const workHours = Array.from({ length: 12 }, (_, i) => i + 6) // 6am to 5pm

  const getHeatmapValue = (day: string, hour: number): number => {
    const entry = heatmapData?.find(h => h.day === day && h.hour === hour)
    return entry?.value || 0
  }

  const maxHeatmapValue = Math.max(...(heatmapData?.map(h => h.value) || [1]))

  const getHeatmapColor = (value: number): string => {
    if (value === 0) return 'hsl(var(--muted))'
    const intensity = Math.min(value / maxHeatmapValue, 1)
    return `hsl(224, 71%, ${60 - intensity * 30}%)`
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total de Horas</p>
            <p className="text-2xl font-bold mt-1">{formatHours(timeReport?.totalHours || 0)}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Horas Faturáveis</p>
            <p className="text-2xl font-bold text-success mt-1">{formatHours(timeReport?.billableHours || 0)}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">% Faturável</p>
            <p className={cn(
              "text-2xl font-bold mt-1",
              (timeReport?.billablePercentage || 0) >= 70 ? "text-success" : "text-warning"
            )}>
              {timeReport?.billablePercentage || 0}%
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Média por Dia</p>
            <p className="text-2xl font-bold mt-1">{formatHours(timeReport?.avgHoursPerDay || 0)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Billable vs Non-billable */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              Faturável vs Não Faturável
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hoursData} layout="vertical" margin={{ left: 0, right: 20 }}>
                  <XAxis type="number" hide />
                  <YAxis 
                    type="category" 
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    width={100}
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatHours(value), 'Horas']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="hours" radius={[0, 4, 4, 0]} barSize={32}>
                    {hoursData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hours by category */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              Horas por Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryData} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <XAxis type="number" hide />
                    <YAxis 
                      type="category" 
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      width={110}
                    />
                    <Tooltip 
                      formatter={(value: number) => [formatHours(value), 'Horas']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="hours" radius={[0, 4, 4, 0]} barSize={24}>
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center">
                <p className="text-muted-foreground">Sem dados de categorias</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Heatmap */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Heatmap de Atividade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Hours header */}
              <div className="flex items-center mb-2">
                <div className="w-12" />
                {workHours.map(hour => (
                  <div key={hour} className="flex-1 text-center text-xs text-muted-foreground">
                    {hour}h
                  </div>
                ))}
              </div>
              
              {/* Days rows */}
              {weekDays.map(day => (
                <div key={day} className="flex items-center gap-1 mb-1">
                  <div className="w-12 text-xs text-muted-foreground">{day}</div>
                  {workHours.map(hour => {
                    const value = getHeatmapValue(day, hour)
                    return (
                      <div
                        key={`${day}-${hour}`}
                        className="flex-1 h-6 rounded-sm transition-colors"
                        style={{ backgroundColor: getHeatmapColor(value) }}
                        title={`${day} ${hour}h: ${formatHours(value)}`}
                      />
                    )
                  })}
                </div>
              ))}

              {/* Legend */}
              <div className="flex items-center justify-end gap-2 mt-4">
                <span className="text-xs text-muted-foreground">Menos</span>
                <div className="flex gap-1">
                  {[0, 0.25, 0.5, 0.75, 1].map(intensity => (
                    <div
                      key={intensity}
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: intensity === 0 ? 'hsl(var(--muted))' : `hsl(224, 71%, ${60 - intensity * 30}%)` }}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">Mais</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
